import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp, NativeStackScreenProps } from '@react-navigation/native-stack';
import { collection, deleteDoc, doc, getDocs, limit, query, where } from 'firebase/firestore';
import { Colors, Radius, Shadow, Typography } from '../theme';
import type { RootStackParamList, TonyteActivity } from '../types';
import { auth, FIRESTORE_COLLECTIONS, firestore } from '../firebase';
import TonyteBackground from '../components/TonyteBackground';
import { canManageEvent } from '../utils/eventPermissions';
import { useProfile } from '../hooks/useProfile';
import { useI18n } from '../i18n';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/themes';

type Props = NativeStackScreenProps<RootStackParamList, 'MyPosts'>;

const MyPostsScreen: React.FC<Props> = ({ route }) => {
  const { userId } = route.params;
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  const insets = useSafeAreaInsets();
  const isIOS = Platform.OS === 'ios';
  const scrollBottomPadding = insets.bottom + (isIOS ? 180 : 80);
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const currentUid = auth.currentUser?.uid || userId;
  const { profile } = useProfile(currentUid);
  const [posts, setPosts] = useState<TonyteActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sources = [
        { collectionName: FIRESTORE_COLLECTIONS.activities, field: 'createdBy' },
        { collectionName: FIRESTORE_COLLECTIONS.activities, field: 'hostId' },
        { collectionName: FIRESTORE_COLLECTIONS.plans, field: 'createdBy' },
        { collectionName: FIRESTORE_COLLECTIONS.plans, field: 'hostId' },
      ];
      const results = await Promise.allSettled(sources.map(source =>
        getDocs(query(
          collection(firestore, source.collectionName),
          where(source.field, '==', userId),
          limit(80),
        )),
      ));
      const map = new Map<string, TonyteActivity>();
      results.forEach((r, index) => {
        if (r.status !== 'fulfilled') return;
        const sourceCollection = sources[index].collectionName;
        r.value.forEach(d => {
          const data = d.data() as TonyteActivity;
          if (sourceCollection === FIRESTORE_COLLECTIONS.activities && data.entryType === 'community_plan') return;
          map.set(`${sourceCollection}/${d.id}`, {
            ...data,
            id: d.id,
            sourceCollection,
            entryType: sourceCollection === FIRESTORE_COLLECTIONS.plans ? 'community_plan' : 'official_event',
          });
        });
      });
      const sorted = Array.from(map.values()).sort((a, b) =>
        (b.createdAt > a.createdAt ? 1 : -1),
      );
      setPosts(sorted);
    } catch {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { void load(); }, [load]);
  useFocusEffect(useCallback(() => { void load(); }, [load]));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return posts;
    return posts.filter(p =>
      [p.title, p.locationName, p.city, p.address, p.category, (p.tags ?? []).join(' ')]
        .some(f => f?.toLowerCase().includes(q)),
    );
  }, [posts, search]);

  const handleEdit = (post: TonyteActivity) => {
    navigation.navigate('UploadEvent', { event: post, mode: 'edit' });
  };

  const handleRepost = (post: TonyteActivity) => {
    const rest = { ...post } as Partial<TonyteActivity> & { updatedAt?: string };
    delete rest.id;
    delete rest.createdAt;
    delete rest.updatedAt;
    navigation.navigate('UploadEvent', { event: rest as TonyteActivity, mode: 'repost' });
  };

  const handleDelete = (post: TonyteActivity) => {
    const userForCheck = profile ? { ...profile, uid: currentUid } : { uid: currentUid };
    if (!canManageEvent(userForCheck, post)) {
      Alert.alert(t('library.permissionRequired'), t('library.deletePermission'));
      return;
    }
    Alert.alert(
      t('library.deletePost'),
      t('library.deleteConfirm').replace('{title}', post.title),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            setDeletingId(post.id);
            try {
              const sourceCollection = post.entryType === 'community_plan'
                ? FIRESTORE_COLLECTIONS.plans
                : FIRESTORE_COLLECTIONS.activities;
              await deleteDoc(doc(firestore, sourceCollection, post.id));
              setPosts(prev => prev.filter(p => p.id !== post.id));
            } catch {
              Alert.alert(t('common.error'), t('library.deletePostFailed'));
            } finally {
              setDeletingId(null);
            }
          },
        },
      ],
    );
  };

  const eventsCount = posts.filter(p => p.entryType === 'official_event').length;
  const plansCount = posts.filter(p => p.entryType === 'community_plan').length;

  return (
    <TonyteBackground appearanceAware style={[styles.container, appearance.container]}>
      {/* Header */}
      <View style={[styles.header, { paddingTop: insets.top + 12 }]}>
        <TouchableOpacity style={[styles.backButton, appearance.control]} onPress={() => navigation.goBack()} activeOpacity={0.78}>
          <Icon name="arrow-left" size={20} color={theme.colors.icon} />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <Text style={styles.headerKicker}>{t('library.profileEyebrow')}</Text>
          <Text style={[styles.headerTitle, appearance.textPrimary]}>{t('library.myPosts')}</Text>
        </View>
        <View style={styles.headerCount}>
          <Text style={styles.headerCountText}>{loading ? '…' : posts.length}</Text>
        </View>
      </View>

      {/* Stats strip */}
      {!loading && posts.length > 0 ? (
        <View style={styles.statsStrip}>
          <View style={[styles.statPill, appearance.control]}>
            <Icon name="tag" size={12} color={Colors.sunset} />
            <Text style={[styles.statPillText, appearance.textSecondary]}>{eventsCount} {t('library.events')}</Text>
          </View>
          <View style={[styles.statPill, appearance.control]}>
            <Icon name="calendar" size={12} color={Colors.turquoise} />
            <Text style={[styles.statPillText, appearance.textSecondary]}>{plansCount} {t('library.plans')}</Text>
          </View>
        </View>
      ) : null}

      {/* Search bar */}
      <View style={[styles.searchWrap, appearance.input]}>
        <Icon name="search" size={16} color={theme.colors.iconMuted} />
        <TextInput
          style={[styles.searchInput, appearance.textPrimary]}
          value={search}
          onChangeText={setSearch}
          placeholder={t('library.search')}
          placeholderTextColor={theme.colors.textMuted}
          returnKeyType="search"
          clearButtonMode="while-editing"
        />
        {search.length > 0 ? (
          <TouchableOpacity onPress={() => setSearch('')} style={styles.searchClear}>
            <Icon name="x" size={14} color={theme.colors.iconMuted} />
          </TouchableOpacity>
        ) : null}
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.scrollContent, { paddingBottom: scrollBottomPadding }]}
        contentInsetAdjustmentBehavior="never"
        automaticallyAdjustContentInsets={false}
        scrollIndicatorInsets={{ bottom: scrollBottomPadding }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {loading ? (
          <View style={styles.emptyWrap}>
            <Text style={[styles.emptyText, appearance.textMuted]}>{t('library.loadingPosts')}</Text>
          </View>
        ) : filtered.length === 0 ? (
          <View style={styles.emptyWrap}>
            <View style={[styles.emptyIcon, appearance.control]}>
              <Icon name="upload" size={28} color={Colors.turquoise} />
            </View>
            <Text style={[styles.emptyTitle, appearance.textPrimary]}>
              {search.trim() ? t('library.noMatches') : t('library.noPosts')}
            </Text>
            <Text style={[styles.emptyText, appearance.textMuted]}>
              {search.trim()
                ? t('library.searchAgain')
                : t('library.noPostsSub')}
            </Text>
          </View>
        ) : (
          filtered.map(post => (
            <PostCard
              key={post.id}
              post={post}
              deleting={deletingId === post.id}
              onEdit={() => handleEdit(post)}
              onRepost={() => handleRepost(post)}
              onDelete={() => handleDelete(post)}
            />
          ))
        )}
      </ScrollView>
    </TonyteBackground>
  );
};

const PostCard: React.FC<{
  post: TonyteActivity;
  deleting: boolean;
  onEdit: () => void;
  onRepost: () => void;
  onDelete: () => void;
}> = ({ post, deleting, onEdit, onRepost, onDelete }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  const isPlan = post.entryType === 'community_plan';
  const coverUri = post.coverPhoto || post.coverImageUrl || post.imageUrl;

  return (
    <View style={[styles.card, appearance.card]}>
      <LinearGradient
        pointerEvents="none"
        colors={!theme.isDark
          ? ['rgba(255,255,255,0.78)', 'rgba(244,225,198,0.34)']
          : ['rgba(255,255,255,0.1)', 'rgba(255,255,255,0.04)']}
        style={StyleSheet.absoluteFill}
      />
      <View style={styles.cardTop}>
        {coverUri ? (
          <Image source={{ uri: coverUri }} style={styles.cardThumb} resizeMode="cover" />
        ) : (
          <View style={[styles.cardThumb, styles.cardThumbEmpty, appearance.control]}>
            <Icon name={isPlan ? 'calendar' : 'tag'} size={22} color={theme.colors.iconMuted} />
          </View>
        )}
        <View style={styles.cardInfo}>
          <View style={[styles.cardTypeBadge, isPlan && styles.cardTypeBadgePlan]}>
            <Text style={[styles.cardTypeText, isPlan && styles.cardTypeTextPlan]}>
              {isPlan ? t('library.communityPlan') : t('library.officialEvent')}
            </Text>
          </View>
          <Text style={[styles.cardTitle, appearance.textPrimary]} numberOfLines={2}>{post.title}</Text>
          <Text style={[styles.cardVenue, appearance.textSecondary]} numberOfLines={1}>
            {post.locationName || post.city || 'Tonyte'}
          </Text>
          {post.startDate ? (
            <Text style={[styles.cardMeta, appearance.textMuted]}>
              {post.startDate}{post.startTime ? ` · ${post.startTime}` : ''}
            </Text>
          ) : null}
        </View>
      </View>

      {post.description ? (
        <Text style={[styles.cardDescription, appearance.textSecondary]} numberOfLines={2}>{post.description}</Text>
      ) : null}

      {post.tags && post.tags.length > 0 ? (
        <View style={styles.tagRow}>
          {post.tags.slice(0, 4).map(tag => (
            <View key={tag} style={[styles.tag, appearance.control]}>
              <Text style={[styles.tagText, appearance.textMuted]}>{tag.startsWith('#') ? tag : `#${tag}`}</Text>
            </View>
          ))}
        </View>
      ) : null}

      <View style={styles.cardActions}>
        {!isPlan ? (
          <TouchableOpacity
            style={[styles.btnEdit, appearance.control, deleting && styles.btnDisabled]}
            onPress={onEdit}
            activeOpacity={0.82}
            disabled={deleting}
          >
            <Icon name="edit-3" size={14} color={theme.colors.icon} />
            <Text style={[styles.btnActionText, appearance.editActionText]}>{t('library.edit')}</Text>
          </TouchableOpacity>
        ) : null}

        {!isPlan ? (
          <TouchableOpacity
            style={[styles.btnRepost, deleting && styles.btnDisabled]}
            onPress={onRepost}
            activeOpacity={0.82}
            disabled={deleting}
          >
            <Icon name="repeat" size={14} color="#fff" />
            <Text style={styles.btnActionText}>{t('library.repost')}</Text>
          </TouchableOpacity>
        ) : null}

        <TouchableOpacity
          style={[styles.btnDeleteIcon, deleting && styles.btnDisabled]}
          onPress={onDelete}
          activeOpacity={0.82}
          disabled={deleting}
        >
          <Icon name="trash-2" size={14} color={Colors.sunset} />
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: 'transparent', overflow: 'visible' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingBottom: 12,
    gap: 12,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.07)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerCenter: { flex: 1 },
  headerKicker: {
    color: Colors.turquoise,
    fontSize: 9,
    fontWeight: Typography.extrabold,
    letterSpacing: 2.2,
    textTransform: 'uppercase',
  },
  headerTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.xl,
    fontWeight: Typography.bold,
    marginTop: 2,
  },
  headerCount: {
    minWidth: 36,
    height: 36,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.3)',
    backgroundColor: 'rgba(38,208,206,0.08)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10,
  },
  headerCountText: {
    color: Colors.turquoise,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  statsStrip: {
    flexDirection: 'row',
    gap: 10,
    paddingHorizontal: 20,
    marginBottom: 12,
  },
  statPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(255,255,255,0.045)',
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  statPillText: {
    color: Colors.textSecondary,
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
  },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 20,
    marginBottom: 16,
    height: 46,
    borderRadius: Radius.lg,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(255,255,255,0.055)',
    paddingHorizontal: 14,
    gap: 10,
  },
  searchInput: {
    flex: 1,
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    paddingVertical: 0,
  },
  searchClear: { padding: 4 },
  scroll: { flex: 1, backgroundColor: 'transparent', overflow: 'visible' },
  scrollContent: {
    width: '100%',
    alignSelf: 'stretch',
    backgroundColor: 'transparent',
    overflow: 'visible',
    paddingHorizontal: 20,
    gap: 12,
  },
  emptyWrap: {
    paddingTop: 60,
    alignItems: 'center',
    gap: 12,
  },
  emptyIcon: {
    width: 64,
    height: 64,
    borderRadius: 22,
    backgroundColor: 'rgba(38,208,206,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.md,
    fontWeight: Typography.bold,
    textAlign: 'center',
  },
  emptyText: {
    color: Colors.textMuted,
    fontSize: Typography.sm,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 280,
  },
  card: {
    width: '100%',
    alignSelf: 'stretch',
    maxWidth: '100%',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(16,23,34,0.72)',
    overflow: 'hidden',
    padding: 16,
    gap: 12,
    ...Shadow.card,
  },
  cardTop: {
    flexDirection: 'row',
    gap: 14,
    width: '100%',
    minWidth: 0,
  },
  cardThumb: {
    width: 72,
    height: 72,
    borderRadius: 16,
    overflow: 'hidden',
    flexShrink: 0,
  },
  cardThumbEmpty: {
    backgroundColor: 'rgba(255,255,255,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardInfo: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  cardTypeBadge: {
    alignSelf: 'flex-start',
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(242,125,38,0.14)',
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  cardTypeBadgePlan: {
    backgroundColor: 'rgba(38,208,206,0.12)',
  },
  cardTypeText: {
    color: Colors.sunset,
    fontSize: 9,
    fontWeight: Typography.extrabold,
    letterSpacing: 1.6,
  },
  cardTypeTextPlan: {
    color: Colors.turquoise,
  },
  cardTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.base,
    fontWeight: Typography.bold,
    lineHeight: 20,
  },
  cardVenue: {
    color: Colors.textSecondary,
    fontSize: Typography.sm,
  },
  cardMeta: {
    color: Colors.textMuted,
    fontSize: Typography.xs,
  },
  cardDescription: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: Typography.sm,
    lineHeight: 19,
  },
  tagRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    width: '100%',
    minWidth: 0,
  },
  tag: {
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(255,255,255,0.07)',
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  tagText: {
    color: Colors.textMuted,
    fontSize: 10,
    fontWeight: Typography.medium,
  },
  cardActions: {
    flexDirection: 'row',
    gap: 10,
    width: '100%',
    minWidth: 0,
  },
  btnEdit: {
    flex: 1,
    minWidth: 0,
    minHeight: 40,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    backgroundColor: 'rgba(255,255,255,0.07)',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  btnRepost: {
    flex: 1,
    minWidth: 0,
    minHeight: 40,
    borderRadius: 14,
    backgroundColor: Colors.turquoise,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    ...Shadow.card,
  },
  btnDeleteIcon: {
    width: 40,
    height: 40,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(242,125,38,0.28)',
    backgroundColor: 'rgba(242,125,38,0.07)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnActionText: {
    color: '#fff',
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
    flexShrink: 1,
  },
  btnDisabled: {
    opacity: 0.45,
  },
});

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: daylightStyle(theme, {
    backgroundColor: 'transparent',
  }),
  input: daylightStyle(theme, {
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.border,
  }),
  control: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.border,
  }),
  card: daylightStyle(theme, {
    backgroundColor: theme.colors.cardBackground,
    borderColor: theme.colors.cardBorder,
    ...theme.shadows.card,
  }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  textMuted: daylightStyle(theme, { color: theme.colors.textMuted }),
  editActionText: daylightStyle(theme, { color: theme.colors.textPrimary }),
});

export default MyPostsScreen;
